(function () {
  'use strict';

  /* ATLAS_PREORDER_THEME_CONTRACT=atl08_preorder_v2
     Universal live-theme adapter. Native Shopify inventory stays at zero
     with policy DENY; Atlas owns the only Pre-Order booking door. */

  /* ATL-101 Tier 1 — 22 Jul 2026
     ---------------------------------------------------------------------
     P0 FIX — variant tracking.
       The Boost theme sets the variant input with `input.value = v.id`
       (a PROPERTY assignment) and dispatches no event. That means BOTH of
       the hooks this file used to rely on were permanently dead:
         * MutationObserver({ attributeFilter: ['value'] }) — a property
           assignment never mutates the `value` ATTRIBUTE, so it never fired.
         * addEventListener('change') — the theme never dispatches one.
       The card therefore locked onto whichever variant the page loaded
       with, so a customer who switched colour never saw the pre-order.
       We now watch the input's value directly (cheap string compare) and
       also react to clicks on the variant selectors. Nothing in the theme
       has to change, so Rule 0 is respected.

     Also in this pass: card payment, the Atlas-approved payment-priority
     and refund copy, the prepaid saving, quantity 1-5, a real focus trap,
     and a booking-completed analytics event.
  */

  var VARIANT_POLL_MS = 400;

  function findProductForm() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.closest('form') : null;
  }

  function mountNearProductForm(root) {
    var form = findProductForm();
    if (!form || root.dataset.atlasUniversalMounted === '1') return;
    var target = form.closest(
      '[data-product-information], product-info, .product__info-container, .product-info, .product-form'
    ) || form;
    if (!target.parentNode) return;
    target.parentNode.insertBefore(root, target);
    root.dataset.atlasUniversalMounted = '1';
  }

  /* Atlas only ever calls three RPCs here, so the full supabase-js bundle
     was pure overhead: measured on the live product page it cost 1,274ms
     to download and pushed first paint of the pre-order card to ~2.0s on a
     sold-out product — the one page where the card IS the conversion path.
     A direct PostgREST fetch removes that dependency entirely. The shim
     keeps the `sb.rpc(fn, args) -> {data, error}` shape so nothing else in
     this file had to change. */
  function createRestClient(config) {
    var restBase = String(config.url).replace(/\/+$/, '') + '/rest/v1/';
    var allowedReadTables = {
      preorder_products: true,
      storefront_product_live_stats: true
    };

    function headers() {
      return {
        'Content-Type': 'application/json',
        apikey: config.anonKey,
        Authorization: 'Bearer ' + config.anonKey
      };
    }

    function parsedResponse(response) {
      return response.text().then(function (raw) {
        var parsed = null;
        try { parsed = raw ? JSON.parse(raw) : null; } catch (error) { parsed = null; }
        if (!response.ok) {
          return {
            data: null,
            error: new Error((parsed && (parsed.message || parsed.hint)) || ('Request failed (' + response.status + ')'))
          };
        }
        return { data: parsed, error: null };
      });
    }

    return {
      rpc: function (fn, args) {
        return fetch(restBase + 'rpc/' + encodeURIComponent(fn), {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify(args || {})
        }).then(parsedResponse).catch(function (error) {
          return { data: null, error: error };
        });
      },
      selectOne: function (table, query) {
        if (!allowedReadTables[table]) {
          return Promise.resolve({ data: null, error: new Error('Unsupported public read') });
        }
        var params = new URLSearchParams();
        Object.keys(query || {}).forEach(function (key) {
          params.set(key, String(query[key]));
        });
        return fetch(restBase + encodeURIComponent(table) + '?' + params.toString(), {
          method: 'GET',
          headers: headers()
        }).then(parsedResponse).then(function (result) {
          if (result.error) return result;
          return { data: Array.isArray(result.data) ? (result.data[0] || null) : result.data, error: null };
        }).catch(function (error) {
          return { data: null, error: error };
        });
      }
    };
  }

  /* Public writes use the protected Edge bridge. It reserves capacity before
     Shopify checkout, owns price and COD truth, and issues a short-lived,
     single-use booking token instead of exposing an anonymous table writer. */
  function createPreorderBridge(config) {
    var endpoint = String(config.url).replace(/\/+$/, '')
      + '/functions/v1/create-preorder';

    function request(payload) {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: config.anonKey,
          Authorization: 'Bearer ' + config.anonKey
        },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.text().then(function (raw) {
          var parsed = null;
          try { parsed = raw ? JSON.parse(raw) : null; } catch (error) { parsed = null; }
          if (!response.ok || !parsed || parsed.success !== true) {
            throw new Error(
              (parsed && parsed.error)
                || ('Secure booking failed (' + response.status + ')')
            );
          }
          return parsed;
        });
      });
    }

    return {
      reserve: function (payload) {
        return request({
          action: 'issue_token',
          preorder_product_id: payload.preorder_product_id,
          variant_id: payload.variant_id,
          session_id: payload.session_id
        }).then(function (token) {
          return solveBookingProof(
            token.proof_challenge,
            Number(token.proof_difficulty || 16)
          ).then(function (counter) {
            return request(Object.assign({}, payload, {
              action: 'create_draft',
              booking_token: token.booking_token,
              proof_counter: counter
            }));
          });
        });
      }
    };
  }

  function leadingZeroBits(bytes) {
    var bits = 0;
    for (var index = 0; index < bytes.length; index += 1) {
      var value = bytes[index];
      if (value === 0) {
        bits += 8;
        continue;
      }
      for (var mask = 128; mask > 0 && (value & mask) === 0; mask >>= 1) {
        bits += 1;
      }
      break;
    }
    return bits;
  }

  /* Difficulty 16 averages about 65k hashes. Parallel batches keep the proof
     quick without blocking taps or animation on lower-end mobile devices. */
  function solveBookingProof(challenge, difficulty) {
    if (
      !window.crypto
      || !window.crypto.subtle
      || typeof window.TextEncoder !== 'function'
    ) {
      return Promise.reject(new Error(
        'Secure booking is not supported on this browser. Please contact Boost support.'
      ));
    }

    var encoder = new window.TextEncoder();
    var batchSize = 192;
    var maxCounter = 2000000;

    function solveBatch(start) {
      var end = Math.min(start + batchSize, maxCounter + 1);
      var digests = [];
      for (var counter = start; counter < end; counter += 1) {
        digests.push(window.crypto.subtle.digest(
          'SHA-256',
          encoder.encode(String(challenge) + ':' + counter)
        ));
      }
      return Promise.all(digests).then(function (results) {
        for (var offset = 0; offset < results.length; offset += 1) {
          if (leadingZeroBits(new Uint8Array(results[offset])) >= difficulty) {
            return start + offset;
          }
        }
        if (end > maxCounter) {
          throw new Error('Secure booking check could not finish. Please try again.');
        }
        return new Promise(function (resolve, reject) {
          window.setTimeout(function () {
            solveBatch(end).then(resolve, reject);
          }, 0);
        });
      });
    }

    return solveBatch(0);
  }

  function money(value) {
    return 'Rs ' + Math.round(Number(value || 0)).toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  /* Pakistani reading order — never show a raw ISO date to a customer. */
  function humanDate(value) {
    if (!value) return '';
    var parsed = new Date(String(value) + 'T00:00:00');
    if (isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function dateParts(value) {
    if (!value) return null;
    var parsed = new Date(String(value) + 'T00:00:00');
    if (isNaN(parsed.getTime())) return null;
    return {
      day: parsed.toLocaleDateString('en-GB', { day: '2-digit' }),
      month: parsed.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
      short: parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      full: parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    };
  }

  function getSessionId() {
    var key = 'atlas_preorder_session';
    try {
      var existing = sessionStorage.getItem(key);
      if (existing && /^s_[A-Za-z0-9_-]{6,100}$/.test(existing)) return existing;
      var random = window.crypto && window.crypto.getRandomValues
        ? Array.from(window.crypto.getRandomValues(new Uint32Array(3))).map(function (v) { return v.toString(36); }).join('')
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      var created = 's_' + random.slice(0, 90);
      sessionStorage.setItem(key, created);
      return created;
    } catch (error) {
      return 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  }

  function customerError(message) {
    var text = String(message || '').toLowerCase();
    if (text.indexOf('phone') >= 0) return 'Please enter a valid WhatsApp number.';
    if (text.indexOf('address') >= 0) return 'Please enter your complete delivery address.';
    if (text.indexOf('city') >= 0) return 'Please enter your city.';
    if (text.indexOf('name') >= 0) return 'Please enter your name.';
    if (text.indexOf('slot') >= 0 || text.indexOf('booked') >= 0) return 'The last available slots were just booked. Please contact Boost support for the next batch.';
    if (text.indexOf('payment') >= 0 || text.indexOf('cod') >= 0 || text.indexOf('bank') >= 0 || text.indexOf('card') >= 0) return 'Please choose one available payment method.';
    if (text.indexOf('available') >= 0 || text.indexOf('closed') >= 0) return 'This pre-order has just closed. Nothing was charged.';
    return 'We could not reserve this pre-order right now. Nothing was charged. Please try again.';
  }

  /* Collection/search discovery --------------------------------------
     A live campaign must not look like an ordinary sold-out item before
     the shopper reaches the PDP. This adapter asks Atlas about only the
     sold-out cards already rendered by the active theme. It changes a card
     only after Atlas returns a customer-ready offer; every other sold-out
     product keeps its existing Notify-me behaviour. */
  function initialiseCollectionPreorders(publicConfig) {
    if (!document.querySelector('[data-atlas-preorder-card-discovery]')) return;
    var cardClient = createRestClient(publicConfig);
    var cards = document.querySelectorAll(
      '[data-bcoll-card][data-variant-id], article[data-product-handle][data-variant-id], .bnl-card[data-product-handle][data-variant-id]'
    );

    cards.forEach(function (card) {
      if (card.getAttribute('data-atlas-preorder-checked') === '1') return;
      var isNewlyLaunchedCard = card.classList.contains('bnl-card');
      var notify = card.querySelector(
        '.bcoll-notify, [data-notify], a[aria-label*="notify" i], a[aria-label*="notified" i]'
      );
      var soldBadge = card.querySelector(
        isNewlyLaunchedCard ? '.bnl-card-soldout' : '.bcoll-badge-soldout, [class*="soldout" i], [class*="sold-out" i]'
      );
      if (!soldBadge || (!notify && !isNewlyLaunchedCard)) return;

      var variantId = String(card.getAttribute('data-variant-id') || '').trim();
      var handle = String(card.getAttribute('data-product-handle') || '').trim();
      if (!variantId || !/^\d+$/.test(variantId) || !handle) return;
      card.setAttribute('data-atlas-preorder-checked', '1');

      cardClient.rpc('get_storefront_preorder_offer', {
        p_shopify_product_id: null,
        p_shopify_variant_id: variantId
      }).then(function (result) {
        var activeOffer = result && result.data;
        var ship = activeOffer && dateParts(activeOffer.customer_shipping_date);
        var prepaidVariant = String(activeOffer && activeOffer.shopify_prepaid_variant_id || '').trim();
        if (result.error || !activeOffer || !activeOffer.id || !ship || !/^\d+$/.test(prepaidVariant)) return;

        var productUrl = '/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(prepaidVariant);
        var title = String(card.getAttribute('data-product-title') || activeOffer.product_name || 'this product');

        soldBadge.textContent = 'PRE-ORDER NOW';
        soldBadge.classList.add('atlas-collection-preorder-badge');
        card.setAttribute('data-atlas-preorder-active', '1');

        card.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
          link.setAttribute('href', productUrl);
        });

        if (notify) {
          notify.removeAttribute('target');
          notify.removeAttribute('rel');
          notify.setAttribute('href', productUrl);
          notify.setAttribute('aria-label', 'Pre-order ' + title + '. Ships ' + ship.full);
          notify.classList.add('atlas-collection-preorder-cta');
          notify.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path>'
            + '<path d="m3.3 7 8.7 5 8.7-5M12 22V12"></path></svg>'
            + '<span>Pre-order now · Ships ' + ship.short + '</span>';
        }

        var priceNode = card.querySelector('.bcoll-now, [data-product-price-current]');
        var compareNode = card.querySelector('.bcoll-was, [data-product-price-compare]');
        var discountNode = card.querySelector('.bcoll-badge.is-sale:not(.is-sale-label)');
        if (priceNode && Number(activeOffer.prepaid_price || activeOffer.preorder_price || 0) > 0) {
          var prepaid = Number(activeOffer.prepaid_price || activeOffer.preorder_price);
          var priceLabel = money(prepaid).replace(/^Rs /, 'Rs. ');
          priceNode.textContent = priceLabel;
          card.setAttribute('data-product-price', priceLabel);
          card.setAttribute('data-price', String(Math.round(prepaid * 100)));

          /* The selected collection card may be Shopify's COD sibling. Hide
             its compare-at price and percentage until the prepaid sibling's
             exact public Shopify variant truth has been read. */
          if (compareNode) compareNode.hidden = true;
          if (discountNode) discountNode.hidden = true;
          fetch('/products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json' }
          }).then(function (response) {
            if (!response.ok) throw new Error('Product price lookup failed');
            return response.json();
          }).then(function (product) {
            var variants = product && Array.isArray(product.variants) ? product.variants : [];
            var prepaidProductVariant = variants.find(function (variant) {
              return String(variant.id) === prepaidVariant;
            });
            var compareAt = Number(prepaidProductVariant && prepaidProductVariant.compare_at_price || 0) / 100;
            if (!(compareAt > prepaid)) return;
            if (compareNode) {
              compareNode.textContent = money(compareAt).replace(/^Rs /, 'Rs. ');
              compareNode.hidden = false;
            }
            if (discountNode) {
              discountNode.textContent = Math.round((compareAt - prepaid) / compareAt * 100) + '% OFF';
              discountNode.hidden = false;
            }
          }).catch(function () {
            /* Exact prepaid price stays visible; ambiguous sale proof stays hidden. */
          });
        }
      }).catch(function () {
        /* Keep the theme's original sold-out card if Atlas is unavailable. */
      });
    });
  }

  function initialisePreorders() {
    var publicConfig = window.BOOST_PUBLIC_SUPABASE_CONFIG;
    if (!publicConfig || !publicConfig.url || !publicConfig.anonKey) return false;

    initialiseCollectionPreorders(publicConfig);

    var roots = document.querySelectorAll('[data-atlas-preorder]');
    roots.forEach(function (candidate, index) {
      if (index > 0) candidate.remove();
    });

    document.querySelectorAll('[data-atlas-preorder]').forEach(function (root) {
    if (root.getAttribute('data-atlas-preorder-initialised') === 'true') return;
    root.setAttribute('data-atlas-preorder-initialised', 'true');
    mountNearProductForm(root);
    var sb;
    var bridge;
    var offer;
    var sessionId = getSessionId();
    var modal = root.querySelector('[data-preorder-modal]');
    var form = root.querySelector('[data-preorder-form]');
    var success = root.querySelector('[data-preorder-success]');
    var errorBox = root.querySelector('[data-preorder-error]');
    var submit = root.querySelector('[data-preorder-submit]');
    var openButton = root.querySelector('[data-preorder-open]');
    var qtySelect = root.querySelector('[data-preorder-qty]');
    var variantInput = document.querySelector('[data-bpdp-variant-input]')
      || document.querySelector('form[action*="/cart/add"] input[name="id"]');
    var lastVariant = root.getAttribute('data-shopify-variant-id') || '';
    var lastFocused = null;

    if (!modal || !form || !submit || !openButton) return;

    /* The modal is relocated to <body> on first open (see openModal), so
       every lookup has to consider both scopes — otherwise the dialog's
       price, ETA and slot text silently stop updating once it moves. */
    function pick(selector) {
      return root.querySelector(selector) || (modal ? modal.querySelector(selector) : null);
    }

    function text(selector, value) {
      var node = pick(selector);
      if (node) node.textContent = value == null ? '' : String(value);
    }

    function attr(selector, name, value) {
      var node = pick(selector);
      if (node && value) node.setAttribute(name, value);
    }

    function show(selector, visible, value) {
      var node = pick(selector);
      if (!node) return;
      if (visible) {
        if (value != null) node.textContent = String(value);
        node.removeAttribute('hidden');
      } else {
        node.setAttribute('hidden', '');
      }
    }

    function track(eventName) {
      /* Anon EXECUTE on the tracking RPC is revoked (bridge migration), so events
         go through the narrow track-preorder-event Edge function. */
      /* Only the three funnel events exist server-side ('completed' is recorded by
         the booking itself). No keepalive: a keepalive request that needs a CORS
         preflight is refused by some browsers, and no navigation follows these calls. */
      if (['shown', 'details_opened', 'started'].indexOf(eventName) < 0) return;
      if (!offer || !offer.id || !publicConfig || !publicConfig.url || !publicConfig.anonKey) return;
      try {
        fetch(String(publicConfig.url).replace(/\/+$/, '') + '/functions/v1/track-preorder-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: publicConfig.anonKey,
            Authorization: 'Bearer ' + publicConfig.anonKey
          },
          body: JSON.stringify({
            preorder_product_id: offer.id,
            event_type: eventName,
            session_id: sessionId
          })
        }).then(function () {}).catch(function () {});
      } catch (error) { /* analytics must never break the page */ }
    }

    function wholeNumber(value) {
      var safe = Math.max(0, Math.floor(Number(value) || 0));
      return safe.toLocaleString('en-PK');
    }

    function resetSocialProof() {
      var proof = root.querySelector('[data-preorder-social-proof]');
      if (proof) {
        proof.classList.remove('is-visible');
        proof.setAttribute('hidden', '');
      }
      document.body.classList.remove('atlas-preorder-social-proof-ready');
    }

    function loadSocialProof(liveOffer, title) {
      var proof = root.querySelector('[data-preorder-social-proof]');
      if (!proof || !liveOffer || !liveOffer.id || !sb) return;
      var expectedOfferId = String(liveOffer.id);
      var productId = String(root.getAttribute('data-shopify-product-id') || '').trim();
      var unitLabel = /\bchair\b/i.test(String(title || '')) ? 'chairs' : 'units';

      resetSocialProof();
      Promise.all([
        sb.selectOne('storefront_product_live_stats', {
          select: 'units_lifetime',
          product_id: 'eq.' + productId,
          limit: '1'
        }),
        sb.selectOne('preorder_products', {
          select: 'current_bookings',
          id: 'eq.' + expectedOfferId,
          is_test_data: 'eq.false',
          test_archived_at: 'is.null',
          limit: '1'
        })
      ]).then(function (results) {
        if (!offer || String(offer.id) !== expectedOfferId) return;
        var lifetime = results[0] && !results[0].error && results[0].data
          ? Math.max(0, Math.floor(Number(results[0].data.units_lifetime) || 0))
          : 0;
        var reserved = results[1] && !results[1].error && results[1].data
          ? Math.max(0, Math.floor(Number(results[1].data.current_bookings) || 0))
          : 0;
        if (lifetime < 1 && reserved < 1) return;

        text('[data-preorder-lifetime-count]', wholeNumber(lifetime));
        text('[data-preorder-unit-label]', unitLabel);
        text('[data-preorder-reserved-count]', wholeNumber(reserved));
        show('[data-preorder-lifetime-proof]', lifetime > 0);
        show('[data-preorder-reserved-proof]', reserved > 0);
        show('[data-preorder-proof-divider]', lifetime > 0 && reserved > 0);
        proof.removeAttribute('hidden');
        window.requestAnimationFrame(function () {
          proof.classList.add('is-visible');
          document.body.classList.add('atlas-preorder-social-proof-ready');
        });
      }).catch(function () {
        resetSocialProof();
      });
    }

    /* Payment helpers ------------------------------------------------- */

    function isPrepaid(method) {
      return method === 'bank_transfer' || method === 'card';
    }

    function basePrice() {
      return Number(offer.preorder_price || offer.original_price || 0);
    }

    function prepaidPrice() {
      var prepaid = Number(offer.prepaid_price || 0);
      return prepaid > 0 ? prepaid : basePrice();
    }

    /* COD carries a heavy-item cash-handling surcharge (Rs 2,000 on chairs).
       The server adds it, so the screen has to show it or the customer is
       surprised at the door. */
    function codPrice() {
      var explicit = Number(offer.cod_price || 0);
      if (explicit > 0) return explicit;
      return basePrice() + Math.max(Number(offer.cod_surcharge || 0), 0);
    }

    function unitPriceFor(method) {
      return isPrepaid(method) ? prepaidPrice() : codPrice();
    }

    function selectedMethod() {
      var checked = form.querySelector('input[name="payment_method"]:checked');
      return checked ? checked.value : '';
    }

    function selectedQuantity() {
      var raw = qtySelect ? Number(qtySelect.value || 1) : 1;
      return Math.max(1, Math.min(isNaN(raw) ? 1 : raw, 5));
    }

    /* Keeps the dialog price honest as the customer changes payment or
       quantity — the prepaid incentive is only persuasive if it is seen. */
    function refreshDialogPrice() {
      if (!offer) return;
      var method = selectedMethod();
      var qty = selectedQuantity();
      var unit = unitPriceFor(method);
      var total = unit * qty;
      var label = money(total);
      if (qty > 1) label += ' · ' + qty + ' × ' + money(unit);
      if (isPrepaid(method) && codPrice() > prepaidPrice()) {
        label += ' · you save ' + money((codPrice() - prepaidPrice()) * qty);
      }
      text('[data-preorder-dialog-price]', label);
    }

    function limitQuantityToSlots(slots) {
      if (!qtySelect) return;
      var max = Math.max(1, Math.min(Number(slots || 1), 5));
      Array.prototype.forEach.call(qtySelect.options, function (option) {
        option.hidden = Number(option.value) > max;
        option.disabled = Number(option.value) > max;
      });
      if (Number(qtySelect.value) > max) qtySelect.value = String(max);
    }

    /* When Atlas owns the pre-order for this variant, suppress the
       third-party pre-order app's injected message so the customer is not
       told two different stories in the same buy box. */
    function silenceRivalPreorderApp(active) {
      document.querySelectorAll('.custom-pre-order-content').forEach(function (node) {
        node.style.display = active ? 'none' : '';
      });
    }

    /* ONE call to action.
       A sold-out variant with a live Atlas pre-order used to show the
       customer three competing actions at once — "Pre-order now", the
       "Get notified on WhatsApp" waitlist card, and the theme's own
       "Notify me" — plus a "Sold out" badge and the line "Currently out of
       stock — tap Notify me below", which flatly contradicts the offer
       above it. The theme already reads a `data-deta-is-preorder` flag in
       updateDeliveryEta() that nothing had ever set; we set it here, which
       is the integration point the theme was written for. */
    function syncStickyPreorderCta(active, liveOffer) {
      document.querySelectorAll('.sticky-buy-bar-atc').forEach(function (button) {
        if (active) {
          if (button.dataset.atlasPreorderCta !== '1') {
            button.dataset.atlasPreorderCta = '1';
            button.dataset.atlasPrevText = button.textContent.trim();
            button.dataset.atlasPrevDisabled = button.disabled ? '1' : '0';
            button.dataset.atlasPrevAria = button.getAttribute('aria-label') || '';
          }
          var stickyShip = liveOffer && liveOffer.customer_shipping_date
            ? dateParts(liveOffer.customer_shipping_date)
            : null;
          button.textContent = stickyShip
            ? 'Pre-order · Ships ' + stickyShip.short
            : 'Pre-order now';
          button.setAttribute(
            'aria-label',
            stickyShip
              ? 'Pre-order now. Ships ' + stickyShip.full
              : 'Pre-order now'
          );
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-disabled', 'disabled');
        } else if (button.dataset.atlasPreorderCta === '1') {
          if (/^Pre-order/i.test(button.textContent.trim())) {
            button.textContent = button.dataset.atlasPrevText || 'Add to Cart';
            button.disabled = button.dataset.atlasPrevDisabled === '1';
            if (button.dataset.atlasPrevAria) {
              button.setAttribute('aria-label', button.dataset.atlasPrevAria);
            } else {
              button.removeAttribute('aria-label');
            }
          }
          delete button.dataset.atlasPreorderCta;
          delete button.dataset.atlasPrevText;
          delete button.dataset.atlasPrevDisabled;
          delete button.dataset.atlasPrevAria;
        }
      });
    }

    function applyPreorderPageState(active, liveOffer) {
      syncStickyPreorderCta(active, liveOffer);
      var deta = document.querySelector('[data-bpdp-deta]');
      if (deta) deta.setAttribute('data-deta-is-preorder', active ? '1' : '0');

      var stockRow = document.querySelector('[data-deta-trust] .bpdp-deta-row');
      if (stockRow) {
        var isOurs = stockRow.getAttribute('data-atlas-preorder-row') === '1';
        if (active) {
          if (!isOurs) {
            stockRow.dataset.atlasPrevHtml = stockRow.innerHTML;
            stockRow.dataset.atlasPrevClass = stockRow.className;
          }
          stockRow.setAttribute('data-atlas-preorder-row', '1');
          stockRow.classList.remove('bpdp-deta-row-warn');
          stockRow.classList.add('bpdp-deta-row-ok');
          var ship = liveOffer && liveOffer.customer_shipping_date
            ? humanDate(liveOffer.customer_shipping_date)
            : '';
          stockRow.innerHTML = '<span class="bpdp-deta-icon" aria-hidden="true">&#128230;</span>'
            + '<span class="bpdp-deta-text"><strong>Available to pre-order</strong>'
            + (ship ? ' &mdash; reserve yours now and it ships from ' + ship + '.' : ' &mdash; reserve yours now.')
            + '</span>';
        } else if (isOurs) {
          stockRow.removeAttribute('data-atlas-preorder-row');
          /* Restore ONLY if our copy is still on screen. On a variant
             switch the theme has already rewritten this row for the newly
             selected colour, and putting our cached HTML back would show
             the previous colour's stock state — which is how an in-stock
             colour ended up reading "Currently out of stock". */
          if (/Available to pre-order/i.test(stockRow.textContent || '')) {
            if (stockRow.dataset.atlasPrevHtml != null) stockRow.innerHTML = stockRow.dataset.atlasPrevHtml;
            if (stockRow.dataset.atlasPrevClass != null) stockRow.className = stockRow.dataset.atlasPrevClass;
          }
        }
      }

      /* The waitlist is the weaker ask when the customer can actually
         reserve the unit. Hidden only while a pre-order is live. */
      var restock = document.querySelector('[data-bpdp-restock]');
      if (restock) restock.style.display = active ? 'none' : '';

      var status = document.querySelector('[data-bpdp-status]');
      if (status) {
        if (active) {
          /* Re-cache on every activation. The theme rewrites this pill on
             each variant change, so a value cached once at page load would
             be stale by the time we restore it. */
          status.dataset.atlasOriginalText = status.textContent.trim();
          status.dataset.atlasOriginalClass = status.className;
          status.textContent = 'Pre-order';
          status.className = 'bpdp-status is-preorder';
        } else if (status.textContent.trim() === 'Pre-order' && status.dataset.atlasOriginalText) {
          /* Only undo our own label. If the theme has already written the
             correct status for the newly selected variant, leave it alone —
             restoring here is what made an in-stock colour read "Sold out". */
          status.textContent = status.dataset.atlasOriginalText;
          status.className = status.dataset.atlasOriginalClass;
        }
      }

      /* The theme's own buy button would otherwise sit under the offer as a
         greyed-out "Notify me" — a third competing action. Turn it into a
         second door to the same pre-order instead. */
      document.querySelectorAll('[data-bpdp-add]').forEach(function (button) {
        if (active) {
          if (!button.dataset.atlasPreorderCta) {
            button.dataset.atlasPreorderCta = '1';
            button.dataset.atlasPrevText = button.textContent.trim();
            button.dataset.atlasPrevDisabled = button.disabled ? '1' : '0';
          }
          button.textContent = 'Pre-order now';
          button.disabled = false;
          /* The theme greys the button via a class, not just [disabled] —
             without this the CTA reads as dead. */
          button.classList.remove('is-disabled');
        } else if (button.dataset.atlasPreorderCta) {
          /* Same rule as the status pill and stock row: only undo our own
             label. The theme relabels this button on every variant change,
             so restoring a cached value would carry the previous colour's
             state across — an in-stock colour showing "Notify me". */
          if (button.textContent.trim() === 'Pre-order now') {
            button.textContent = button.dataset.atlasPrevText || 'Add to Cart';
            button.disabled = button.dataset.atlasPrevDisabled === '1';
          }
          delete button.dataset.atlasPreorderCta;
          delete button.dataset.atlasPrevText;
          delete button.dataset.atlasPrevDisabled;
        }
      });

      /* Theme-independent fallback: Shopify product forms use /cart/add even
         when their class names and sections differ. The native button opens
         the Atlas booking dialog and never submits the sold-out variant. */
      var nativeForm = findProductForm();
      if (nativeForm) {
        nativeForm.querySelectorAll('button[type="submit"], [name="add"], input[type="submit"]').forEach(function (button) {
          if (active) {
            if (!button.dataset.atlasPreorderCta) {
              button.dataset.atlasPreorderCta = '1';
              button.dataset.atlasPrevText = button.textContent ? button.textContent.trim() : '';
              button.dataset.atlasPrevValue = button.value || '';
              button.dataset.atlasPrevDisabled = button.disabled ? '1' : '0';
            }
            if (button.tagName === 'INPUT') button.value = 'Pre-order now';
            else button.textContent = 'Pre-order now';
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-disabled', 'disabled');
          } else if (button.dataset.atlasPreorderCta === '1') {
            if (button.tagName === 'INPUT') button.value = button.dataset.atlasPrevValue || 'Add to cart';
            else button.textContent = button.dataset.atlasPrevText || 'Add to cart';
            button.disabled = button.dataset.atlasPrevDisabled === '1';
            delete button.dataset.atlasPreorderCta;
            delete button.dataset.atlasPrevText;
            delete button.dataset.atlasPrevValue;
            delete button.dataset.atlasPrevDisabled;
          }
        });
        nativeForm.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]').forEach(function (node) {
          node.style.display = active ? 'none' : '';
        });
      }

      /* Buy Now would take a pre-order straight to Shopify checkout and
         bypass the Atlas booking entirely. */
      document.querySelectorAll('a.bpdp-btn-dark, [data-bpdp-sticky-add]').forEach(function (node) {
        node.style.display = active ? 'none' : '';
      });

      /* Some Boost products sell Prepaid and COD as SEPARATE Shopify
         variants. Only one of them carries the campaign, so tapping the
         other payment pill silently threw the customer out of the
         pre-order and back to "Sold out / Notify me" — and the COD variant
         also carries a +Rs 2,000 handling price the campaign cannot
         charge. Payment is chosen inside the Atlas form, so this selector
         is hidden while a pre-order is live. */
      document.querySelectorAll('.bpdp-options').forEach(function (block) {
        var label = block.querySelector('.bpdp-options-label');
        if (!label || !/payment\s*method/i.test(label.textContent || '')) return;
        block.style.display = active ? 'none' : '';
      });

      /* A "SOLD OUT" flag on the swatch directly contradicts the offer. */
      document.querySelectorAll('.bpdp-swatch.is-selected .bpdp-swatch-soldtag').forEach(function (tagNode) {
        tagNode.style.display = active ? 'none' : '';
      });

      /* The theme's static "Prepaid or COD · COD adds Rs. 2,000" reassurance
         is about normal checkout. On a prepaid-only pre-order it promises a
         payment method the booking form does not offer. */
      var hideCodLine = active && !!(liveOffer && (liveOffer.prepaid_only || !liveOffer.payment_cod_enabled));
      document.querySelectorAll('[data-deta-trust] .bpdp-deta-row').forEach(function (row) {
        if (row.getAttribute('data-atlas-preorder-row') === '1') return;
        if (!/COD adds|Prepaid or COD/i.test(row.textContent || '')) return;
        if (hideCodLine) {
          row.dataset.atlasCodHidden = '1';
          row.style.display = 'none';
        } else if (row.dataset.atlasCodHidden === '1') {
          row.style.display = '';
          delete row.dataset.atlasCodHidden;
        }
      });
    }

    function preferredPrepaidMethod() {
      if (offer && offer.payment_bank_transfer_enabled) return 'bank_transfer';
      if (offer && offer.payment_card_enabled) return 'card';
      return '';
    }

    function reflectApprovedPayment(nextMethod) {
      root.querySelectorAll('[data-preorder-choice]').forEach(function (button) {
        var choice = button.getAttribute('data-preorder-choice');
        var selectedKind = nextMethod === 'cod' ? 'cod' : 'prepaid';
        button.setAttribute('aria-pressed', choice === selectedKind ? 'true' : 'false');
      });
      text(
        '[data-preorder-cta-label]',
        nextMethod === 'cod'
          ? 'Reserve COD · ' + money(codPrice())
          : 'Reserve prepaid · ' + money(prepaidPrice())
      );
      refreshDialogPrice();
    }

    function selectApprovedPayment(kind) {
      if (!offer) return;
      var nextMethod = kind === 'cod' ? 'cod' : preferredPrepaidMethod();
      if (!nextMethod) nextMethod = offer.payment_cod_enabled ? 'cod' : '';

      form.querySelectorAll('input[name="payment_method"]').forEach(function (radio) {
        radio.checked = radio.value === nextMethod;
      });
      reflectApprovedPayment(nextMethod);
    }

    root.querySelectorAll('[data-preorder-choice]').forEach(function (button) {
      button.addEventListener('click', function () {
        selectApprovedPayment(button.getAttribute('data-preorder-choice'));
      });
    });

    function updateApprovedOffer(nextOffer) {
      offer = nextOffer;
      var prepaid = prepaidPrice();
      var cod = codPrice();
      var codExtra = Math.max(cod - prepaid, 0);
      var title = offer.product_name || root.getAttribute('data-product-title') || 'product';
      var slots = Number(offer.remaining_slots || 0);
      var ship = dateParts(offer.customer_shipping_date);
      var arrival = dateParts(offer.warehouse_arrival_date);
      var prepaidEnabled = !!(offer.payment_bank_transfer_enabled || offer.payment_card_enabled);
      var codEnabled = !!offer.payment_cod_enabled;

      if (!ship || !arrival || slots < 1) {
        clearOffer();
        return;
      }

      text('[data-preorder-title]', 'Reserve your ' + title);
      text('[data-preorder-ship-day]', ship.day);
      text('[data-preorder-ship-month]', ship.month);
      text('[data-preorder-ship-exact]', 'Ships ' + ship.full);
      var qualityCheckDays = Math.floor((new Date(String(offer.customer_shipping_date) + 'T00:00:00Z') - new Date(String(offer.warehouse_arrival_date) + 'T00:00:00Z')) / 86400000);
      text('[data-preorder-buffer]', 'Expected ' + arrival.short + (qualityCheckDays >= 1 ? ' · ' + qualityCheckDays + '-day quality check included' : ''));
      text('[data-preorder-arrival-short]', arrival.short);
      text('[data-preorder-ship-short]', ship.short);
      text('[data-preorder-prepaid-price]', money(prepaid));
      text('[data-preorder-prepaid-saving]', codExtra > 0 ? 'Save ' + money(codExtra) : 'Pay securely online');
      text('[data-preorder-cod-price]', money(cod));
      text(
        '[data-preorder-cod-note]',
        codExtra > 0
          ? money(Number(offer.cod_surcharge || codExtra)) + ' handling included'
          : 'No handling surcharge'
      );

      var prepaidChoice = root.querySelector('[data-preorder-choice="prepaid"]');
      var codChoice = root.querySelector('[data-preorder-choice="cod"]');
      if (prepaidChoice) prepaidChoice.hidden = !prepaidEnabled;
      if (codChoice) codChoice.hidden = !codEnabled;

      text('[data-preorder-dialog-title]', 'Reserve ' + title);
      text('[data-preorder-dialog-eta]', 'Ships ' + ship.full + ' · no charge is taken on this screen');
      text('[data-preorder-dialog-product]', title);
      text('[data-preorder-dialog-slots]', 'Available for the next shipment');
      if (offer.image_url) attr('[data-preorder-dialog-image]', 'src', offer.image_url);

      var payMessage = String(offer.customer_payment_message || '');
      show('[data-preorder-payment-note]', !!payMessage, payMessage);
      show('[data-preorder-refund-note]', !!offer.refund_policy_message, offer.refund_policy_message);

      var optionNotes = [
        ['[data-preorder-bank-option] small', money(prepaid) + ' · continue to secure Shopify checkout'],
        ['[data-preorder-card-option] small', money(prepaid) + ' · continue to secure Shopify checkout'],
        ['[data-preorder-cod-option] small', money(cod)
          + (codExtra > 0 ? ' · includes ' + money(Number(offer.cod_surcharge || codExtra)) + ' handling' : '')
          + ' · complete COD in Shopify checkout']
      ];
      optionNotes.forEach(function (pair) {
        var node = pick(pair[0]);
        if (node) node.textContent = pair[1];
      });

      var bankOption = pick('[data-preorder-bank-option]');
      var cardOption = pick('[data-preorder-card-option]');
      var codOption = pick('[data-preorder-cod-option]');
      if (bankOption) bankOption.hidden = !offer.payment_bank_transfer_enabled;
      if (cardOption) cardOption.hidden = !offer.payment_card_enabled;
      if (codOption) codOption.hidden = !codEnabled;

      form.querySelectorAll('input[name="payment_method"]').forEach(function (radio) {
        radio.disabled = radio.value === 'bank_transfer'
          ? !offer.payment_bank_transfer_enabled
          : radio.value === 'card'
          ? !offer.payment_card_enabled
          : !codEnabled;
      });

      var currentVariant = String(lastVariant || '');
      var codVariant = String(offer.shopify_cod_variant_id || '');
      var initialKind = codEnabled && (
        offer.requested_payment_method === 'cod'
        || (currentVariant && currentVariant === codVariant)
      )
        ? 'cod'
        : prepaidEnabled ? 'prepaid' : 'cod';
      selectApprovedPayment(initialKind);
      limitQuantityToSlots(slots);

      root.removeAttribute('hidden');
      silenceRivalPreorderApp(true);
      applyPreorderPageState(true, offer);
      loadSocialProof(offer, title);
      track('shown');
    }

    function clearOffer() {
      offer = null;
      root.setAttribute('hidden', '');
      resetSocialProof();
      silenceRivalPreorderApp(false);
      applyPreorderPageState(false, null);
    }

    function loadOffer(variantId) {
      clearOffer();
      return sb.rpc('get_storefront_preorder_offer', {
        p_shopify_product_id: root.getAttribute('data-shopify-product-id'),
        p_shopify_variant_id: variantId || null
      }).then(function (result) {
        if (result.error || !result.data || !result.data.id) return;
        updateApprovedOffer(result.data);
      }).catch(function () {
        clearOffer();
      });
    }

    /* Focus management ------------------------------------------------ */

    function focusables() {
      var panel = modal.querySelector('.atlas-preorder-modal__panel');
      if (!panel) return [];
      return Array.prototype.filter.call(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'),
        function (node) { return node.offsetParent !== null; }
      );
    }

    function openModal() {
      if (!offer) return;
      /* The card sits inside the product summary, which is itself inside a
         stacking context the sticky site header wins against — on mobile
         that put the header over the dialog and made the close button
         physically untappable. Re-parenting to <body> takes the dialog out
         of that context; its own z-index then applies. */
      if (modal.parentElement !== document.body) document.body.appendChild(modal);
      lastFocused = document.activeElement;
      modal.removeAttribute('hidden');
      document.body.classList.add('atlas-preorder-modal-open');
      form.removeAttribute('hidden');
      success.setAttribute('hidden', '');
      errorBox.setAttribute('hidden', '');
      track('details_opened');
      var first = form.querySelector('input[name="customer_name"]');
      if (first) window.setTimeout(function () { first.focus(); }, 80);
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      document.body.classList.remove('atlas-preorder-modal-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openButton.addEventListener('click', openModal);
    root.querySelectorAll('[data-preorder-close]').forEach(function (node) {
      node.addEventListener('click', closeModal);
    });

    /* While a pre-order is live the theme's buy button is relabelled
       "Pre-order now". Capture the click before the cart form submits so it
       opens the Atlas booking rather than adding an unbuyable variant. */
    document.addEventListener('click', function (event) {
      if (!offer || !event.target || !event.target.closest) return;
      var cta = event.target.closest(
        '[data-bpdp-add], .sticky-buy-bar-atc, form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] input[type="submit"]'
      );
      if (!cta || cta.dataset.atlasPreorderCta !== '1') return;
      event.preventDefault();
      event.stopPropagation();
      openModal();
    }, true);

    document.addEventListener('keydown', function (event) {
      if (modal.hasAttribute('hidden')) return;
      if (event.key === 'Escape') { closeModal(); return; }
      if (event.key !== 'Tab') return;
      var nodes = focusables();
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    form.addEventListener('change', function (event) {
      if (!event.target) return;
      if (event.target.name === 'payment_method') {
        reflectApprovedPayment(event.target.value);
      } else if (event.target.name === 'quantity') {
        refreshDialogPrice();
      }
    });

    var customerFieldRules = {
      customer_name: function (value) {
        return String(value || '').trim().length >= 2
          ? ''
          : 'Enter the customer’s full name.';
      },
      customer_phone: function (value) {
        var digits = String(value || '').replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15
          ? ''
          : 'Enter 10–15 digits, e.g. 0300 1234567.';
      },
      shipping_city: function (value) {
        return String(value || '').trim().length >= 2
          ? ''
          : 'Enter the complete city name.';
      },
      shipping_address: function (value) {
        return String(value || '').trim().length >= 8
          ? ''
          : 'Enter the complete delivery address (at least 8 characters).';
      }
    };

    function showCustomerFieldError(field, message) {
      if (!field) return false;
      var errorNode = form.querySelector(
        '[data-preorder-field-error="' + field.name + '"]'
      );
      var fieldLabel = field.closest('label');
      var hintNode = fieldLabel && fieldLabel.querySelector('.atlas-preorder-form__hint');
      field.setCustomValidity(message || '');
      if (message) {
        field.setAttribute('aria-invalid', 'true');
        if (hintNode) hintNode.setAttribute('hidden', '');
        if (errorNode) {
          errorNode.textContent = message;
          errorNode.removeAttribute('hidden');
        }
      } else {
        field.removeAttribute('aria-invalid');
        if (hintNode) hintNode.removeAttribute('hidden');
        if (errorNode) {
          errorNode.textContent = '';
          errorNode.setAttribute('hidden', '');
        }
      }
      return !!message;
    }

    function validateCustomerField(field) {
      var rule = field && customerFieldRules[field.name];
      return rule
        ? showCustomerFieldError(field, rule(field.value))
        : false;
    }

    function validateCustomerFields() {
      var firstInvalid = null;
      Object.keys(customerFieldRules).forEach(function (name) {
        var field = form.elements.namedItem(name);
        if (validateCustomerField(field) && !firstInvalid) firstInvalid = field;
      });
      if (firstInvalid) {
        errorBox.textContent = 'Please correct the highlighted field.';
        errorBox.removeAttribute('hidden');
        firstInvalid.focus();
        firstInvalid.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });
        return false;
      }
      errorBox.setAttribute('hidden', '');
      return true;
    }

    function showServerFieldError(error) {
      var message = String(error && error.message || '').toLowerCase();
      var fieldName = message.indexOf('phone') >= 0 || message.indexOf('whatsapp') >= 0
        ? 'customer_phone'
        : message.indexOf('address') >= 0
        ? 'shipping_address'
        : message.indexOf('city') >= 0
        ? 'shipping_city'
        : message.indexOf('name') >= 0
        ? 'customer_name'
        : '';
      if (!fieldName) return false;
      var field = form.elements.namedItem(fieldName);
      var friendly = customerError(error && error.message);
      showCustomerFieldError(field, friendly);
      errorBox.textContent = 'Please correct the highlighted field.';
      errorBox.removeAttribute('hidden');
      field.focus();
      field.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      return true;
    }

    form.addEventListener('input', function (event) {
      if (!event.target || !customerFieldRules[event.target.name]) return;
      /* Do not turn a field red while the customer is still typing. Once a
         mistake has been shown, clear it immediately as they correct it. */
      if (event.target.getAttribute('aria-invalid') === 'true') {
        validateCustomerField(event.target);
      }
      if (!form.querySelector('[aria-invalid="true"]')) {
        errorBox.setAttribute('hidden', '');
      }
    });

    form.addEventListener('focusout', function (event) {
      if (!event.target || !customerFieldRules[event.target.name]) return;
      validateCustomerField(event.target);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!offer || submit.disabled) return;
      if (!validateCustomerFields()) return;
      if (!form.reportValidity()) return;

      var fields = new FormData(form);
      var payload = {
        preorder_product_id: offer.id,
        variant_id: offer.variant_id,
        customer_name: fields.get('customer_name'),
        customer_phone: fields.get('customer_phone'),
        shipping_city: fields.get('shipping_city'),
        shipping_address: fields.get('shipping_address'),
        payment_method: fields.get('payment_method'),
        quantity: selectedQuantity(),
        session_id: sessionId
      };

      errorBox.setAttribute('hidden', '');
      submit.disabled = true;
      submit.textContent = 'Securing your slot…';
      track('started');

      bridge.reserve(payload)
        .then(function (booking) {
          if (!booking.secure_checkout_url) {
            throw new Error('Secure Shopify checkout was not created');
          }
          track('completed');
          form.setAttribute('hidden', '');
          success.removeAttribute('hidden');
          text('[data-preorder-success-number]', booking.preorder_number);
          text(
            '[data-preorder-success-copy]',
            booking.message
              || 'Your verified slot is reserved. Complete the secure Shopify checkout to confirm your Pre-Order.'
          );
          var nextLink = pick('[data-preorder-success-zaara]');
          if (nextLink) {
            nextLink.textContent = 'Continue to secure Shopify checkout';
            nextLink.href = booking.secure_checkout_url;
          }
          if (success) success.focus && success.focus();
        })
        .catch(function (error) {
          if (showServerFieldError(error)) return;
          errorBox.textContent = customerError(error && error.message);
          errorBox.removeAttribute('hidden');
        })
        .finally(function () {
          submit.disabled = false;
          submit.textContent = 'Continue to secure checkout';
        });
    });

    sb = createRestClient(publicConfig);
    bridge = createPreorderBridge(publicConfig);
    loadOffer(lastVariant);
    markPreorderableSwatches();

    /* A colour that is sold out but pre-orderable was still wearing the
       theme's red "SOLD OUT" swatch flag while a DIFFERENT colour was
       selected — so a customer looking at the in-stock colour saw the
       pre-order colour as unavailable and never clicked it. Ask Atlas once
       per colour whether a campaign exists and relabel the flag.
       Degrades safely: if a lookup fails the flag simply stays as it was. */
    function markPreorderableSwatches() {
      if (!sb) return;
      var meta = window.ShopifyAnalytics
        && window.ShopifyAnalytics.meta
        && window.ShopifyAnalytics.meta.product;
      if (!meta || !meta.variants) return;

      document.querySelectorAll('.bpdp-swatch[data-bpdp-option-value]').forEach(function (swatch) {
        var flag = swatch.querySelector('.bpdp-swatch-soldtag');
        if (!flag || flag.dataset.atlasChecked === '1') return;
        flag.dataset.atlasChecked = '1';

        var wanted = swatch.getAttribute('data-bpdp-option-value');
        /* Match the FIRST option exactly. A loose "contains" test would make
           "Grey/Black" match a swatch called "Black". */
        var match = null;
        for (var i = 0; i < meta.variants.length; i++) {
          var pt = String(meta.variants[i].public_title || '');
          if (pt.split(' / ')[0] === wanted) { match = meta.variants[i]; break; }
        }
        if (!match) return;

        sb.rpc('get_storefront_preorder_offer', {
          p_shopify_product_id: root.getAttribute('data-shopify-product-id'),
          p_shopify_variant_id: String(match.id)
        }).then(function (result) {
          if (result.error || !result.data || !result.data.id) return;
          flag.textContent = 'Pre-order';
          flag.classList.add('atlas-swatch-preorder');
        });
      });
    }

    /* P0 — variant tracking that actually works on this theme. */
    function syncVariant() {
      if (!sb || !variantInput) return;
      var nextVariant = variantInput.value || '';
      if (nextVariant && nextVariant !== lastVariant) {
        lastVariant = nextVariant;
        root.setAttribute('data-shopify-variant-id', nextVariant);
        loadOffer(nextVariant);
      } else if (nextVariant && offer) {
        /* The Boost sticky bar is re-rendered by its own theme script. Keep
           its one-line Pre-Order promise aligned without touching any
           product details or other theme controls. */
        syncStickyPreorderCta(true, offer);
      }
    }

    if (variantInput) {
      window.setInterval(syncVariant, VARIANT_POLL_MS);
      variantInput.addEventListener('change', syncVariant);
      /* React the moment a swatch is tapped, so the card does not lag
         behind the price the theme has already updated. */
      document.addEventListener('click', function (event) {
        if (!event.target || !event.target.closest) return;
        if (event.target.closest('.bpdp-swatch, .bpdp-variant, [data-bpdp-swatch], [data-bpdp-variant]')) {
          window.setTimeout(syncVariant, 60);
          window.setTimeout(syncVariant, 320);
        }
      }, true);
    }
    });
    return true;
  }

  /* live-viewers.js owns the existing public browser configuration. Shopify
     may execute product-section assets before that shared file, or replace a
     section after DOMContentLoaded. Retry briefly and initialise each section
     only once instead of exposing a blank or permanently hidden card. */
  function bootPreorders(attempt) {
    if (initialisePreorders()) return;
    if (attempt < 30) {
      window.setTimeout(function () { bootPreorders(attempt + 1); }, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootPreorders(0); }, { once: true });
  } else {
    bootPreorders(0);
  }
  document.addEventListener('shopify:section:load', function () { bootPreorders(0); });
})();
